import { idiv, imax, mulBp, withinRange } from '../math/fixed.ts';
import type { BattleState } from '../types.ts';
import { damageEnemy, ev, isFlying } from './combat.ts';

/**
 * The old trap (La Quemadura 3, docs/QUEMADURA.md): the first Vigil's harpoon engine, broken but whole, at the edge of
 * the clearing, aimed across it. A glass colossus blocks the way and shrugs off almost every blow. Heroes standing by
 * its two winches wind it; wound, a hero at its lever pulls it, and a moment later the harpoon flies down its line,
 * through everything in it, from a few paces out (right at its mouth the harpoon flies over). The colossus has to be
 * brought into the line first. Each shot needs winding again.
 */

/** How far along the line a point lies, and how far off it (the axis is `ax, ay` in bp). */
const inLine = (s: BattleState, x: number, y: number): boolean => {
  const r = s.rules?.trap;
  if (!r) return false;
  const dx = x - r.at.x;
  const dy = y - r.at.y;
  const along = idiv(dx * r.ax + dy * r.ay, 10000);
  const off = idiv(dx * -r.ay + dy * r.ax, 10000);
  return along >= r.near && along <= r.len && off >= -r.half && off <= r.half;
};

export const trapTick = (s: BattleState): void => {
  const r = s.rules?.trap;
  if (!r) return;
  const st = (s.trap ??= { wound: r.winches.map(() => 0), armed: false, fireAt: 0, shots: 0, hits: 0 });
  if (!s.started) return;
  const standing = s.heroes.filter((h) => h.downTicks === 0);
  if (!st.armed && !st.fireAt) {
    // Winding: each winch turns while a hero stands by it.
    r.winches.forEach((w, i) => {
      if ((st.wound[i] ?? 0) >= r.wind || !standing.some((h) => withinRange(h.x, h.y, w.x, w.y, r.reach))) return;
      st.wound[i] = (st.wound[i] ?? 0) + 1;
      if (st.wound[i] === r.wind) ev(s, { t: 'trap', kind: 'wound', x: w.x, y: w.y });
    });
    if (st.wound.every((n) => n >= r.wind)) {
      st.armed = true;
      ev(s, { t: 'trap', kind: 'armed', x: r.at.x, y: r.at.y });
    }
  } else if (st.armed && standing.some((h) => withinRange(h.x, h.y, r.lever.x, r.lever.y, r.reach))) {
    // Pulled: the harpoon flies a moment later (its blow is the puller's).
    st.armed = false;
    st.by = standing.find((h) => withinRange(h.x, h.y, r.lever.x, r.lever.y, r.reach))?.pid ?? '';
    st.fireAt = s.tick + r.delay;
    ev(s, { t: 'trap', kind: 'pull', x: r.lever.x, y: r.lever.y });
  }
  if (!st.fireAt || s.tick < st.fireAt) return;
  st.fireAt = 0;
  st.shots++;
  st.wound = r.winches.map(() => 0);
  ev(s, { t: 'trap', kind: 'shot', x: r.at.x, y: r.at.y });
  let hit = false;
  for (const e of s.enemies) {
    if (e.hp <= 0 || isFlying(e) || !inLine(s, e.x, e.y)) continue;
    const boss = e.id === s.bossId;
    if (boss) hit = true;
    damageEnemy(s, e, boss ? imax(1, mulBp(e.maxHp, r.hitBp)) : r.foe, { pid: st.by ?? '', ignoresArmor: true, noReact: true, harpoon: true });
    ev(s, { t: 'trap', kind: boss ? 'hit' : 'skewer', x: e.x, y: e.y });
  }
  if (hit) st.hits++;
  else ev(s, { t: 'trap', kind: 'miss', x: r.at.x, y: r.at.y });
};

/** The colossus shrugs off what isn't the harpoon. */
export const trapShield = (s: BattleState, id: number, amount: number, harpoon: boolean): number => {
  const r = s.rules?.trap;
  return r && !harpoon && id === s.bossId ? mulBp(amount, r.shieldBp) : amount;
};

/** Whether a point lies in the harpoon's line (the renderer's mark on the ground, and the tests). */
export const inTrapLine = inLine;
